import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Segmented,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  notification,
  Empty,
  Spin,
} from 'antd';
import {
  ApiOutlined,
  ArrowLeftOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  CrownOutlined,
  DeleteOutlined,
  KeyOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  RobotOutlined,
  SafetyCertificateOutlined,
  SwapOutlined,
  TeamOutlined,
  UnorderedListOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { usePermissions } from '../../../assets/utils/usePermissions';
import lib from '../../../assets/utils/lib';
import DashboardHero from '../../dashboard/components/DashboardHero';
import DashboardSection from '../../dashboard/components/DashboardSection';
import { TableSkeleton } from '../../dashboard/components/DashboardSkeleton';
import { ADMIN_REVIEW_MODAL_CLASS, hasMetricValue } from '../../dashboard/utils';
import './partners-dashboard.css';
import { renderAdminModule } from './modules/registry';
import {
  resolvePartnerDetailTabFromSearch,
  paAgentDecisionsFiltersFromSearch,
} from './partnerTabUtils';
import {
  CapabilityPickerField,
  PartnerCapabilitiesPanel,
  PaAgentOutboundConnectorPanel,
  PortalManifestPreview,
  partnerHasOutboundConnector,
  normalizeCapabilitiesCatalog,
} from './PartnerCapabilitiesEditor';

const { Text, Link } = Typography;

const MODAL_PROPS = {
  rootClassName: ADMIN_REVIEW_MODAL_CLASS,
  className: `${ADMIN_REVIEW_MODAL_CLASS}__dialog`,
};

const MODULE_ICONS = {
  robot: RobotOutlined,
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

function unwrapBody(resp) {
  return resp?.data || resp;
}

function apiErrorMessage(body, fallback) {
  if (!body) return fallback;
  const code = body.code || body.error_code;
  const msg = body.message || body.msg;
  if (code && msg) return `${msg} (${code})`;
  return msg || code || fallback;
}

function normalizePartnersList(resp) {
  const body = unwrapBody(resp);
  if (Number(body?.error) && body?.error !== 0) return [];
  const data = body?.data ?? body;
  if (Array.isArray(data?.partners)) return data.partners;
  if (Array.isArray(data)) return data;
  return [];
}

function renderBadges(badges) {
  if (!Array.isArray(badges) || !badges.length) return null;
  return (
    <Space size={4} wrap>
      {badges.map((b) => (
        <Tag key={b.key || b.label}>{b.label || b.key}</Tag>
      ))}
    </Space>
  );
}

function pickCount(row, ...keys) {
  for (const k of keys) {
    const v = row[k];
    if (v != null && v !== '') return v;
  }
  return null;
}

const DEFAULT_ADMIN_SHELL = ['users', 'keys', 'audit'];

function PartnersList({ capabilityCatalog = [] }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.listIntegrationPartners();
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Could not load partners') });
        setRows([]);
        return;
      }
      setRows(normalizePartnersList(resp));
    } catch (e) {
      notification.error({ message: 'Could not load partners', description: e?.message });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submitCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);
      const payload = {
        slug: String(values.slug).trim().toLowerCase(),
        name: String(values.name).trim(),
      };
      if (Array.isArray(values.capabilities) && values.capabilities.length) {
        payload.capabilities = values.capabilities;
      }
      const resp = await lib.createIntegrationPartner(payload);
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Create failed') });
        return;
      }
      message.success('Partner created');
      setCreateOpen(false);
      form.resetFields();
      await load();
      const created = body?.data?.partner || body?.data;
      const id = created?.id;
      if (id != null) navigate(`/admin/integrations/partners/${id}`);
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: 'Create failed', description: e?.message });
    } finally {
      setCreating(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (v, row) => (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/admin/integrations/partners/${row.id}`)}>
            {v || '—'}
          </Button>
        ),
      },
      { title: 'Slug', dataIndex: 'slug', key: 'slug', render: (v) => <code>{v || '—'}</code> },
      {
        title: 'Capabilities',
        key: 'badges',
        render: (_, row) => renderBadges(row.badges) || '—',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 110,
        render: (v) => (
          <Tag color={v === 'active' ? 'green' : 'default'}>{v || '—'}</Tag>
        ),
      },
      {
        title: 'Active users',
        key: 'users',
        width: 120,
        render: (_, row) => pickCount(row, 'active_users', 'active_user_count', 'users_active') ?? '—',
      },
      {
        title: 'Active keys',
        key: 'keys',
        width: 120,
        render: (_, row) => pickCount(row, 'active_keys', 'active_key_count', 'keys_active') ?? '—',
      },
      {
        title: 'Created',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (v) => fmtDateTime(v),
      },
    ],
    [navigate],
  );

  const heroMetrics = useMemo(() => {
    if (!rows.length) return [];
    const active = rows.filter((r) => r.status === 'active').length;
    const userTotal = rows.reduce(
      (sum, r) => sum + (Number(pickCount(r, 'active_users', 'active_user_count', 'users_active')) || 0),
      0,
    );
    const keyTotal = rows.reduce(
      (sum, r) => sum + (Number(pickCount(r, 'active_keys', 'active_key_count', 'keys_active')) || 0),
      0,
    );
    return [
      {
        key: 'total',
        title: 'Partners',
        value: rows.length,
        color: '#3497D3',
        icon: <ApiOutlined />,
        subtitle: 'Integration accounts',
      },
      {
        key: 'active',
        title: 'Active',
        value: active,
        color: '#49B870',
        icon: <SafetyCertificateOutlined />,
        subtitle: 'Enabled partners',
      },
      {
        key: 'users',
        title: 'Portal users',
        value: userTotal,
        color: '#722ed1',
        icon: <TeamOutlined />,
        subtitle: 'Active across partners',
      },
      {
        key: 'keys',
        title: 'API keys',
        value: keyTotal,
        color: '#0ea5e9',
        icon: <KeyOutlined />,
        subtitle: 'Active inbound keys',
      },
    ].filter((m) => hasMetricValue(m.value));
  }, [rows]);

  return (
    <div className="dashboard-page-stack">
      <header className="dash-masthead">
        <div className="dash-masthead__lead">
          <h1 className="dash-masthead__title">Integration partners</h1>
          <p className="dash-masthead__meta">
            Manage partner portals, capabilities, and API access
          </p>
        </div>
      </header>

      <section className="partners-overview dash-overview dash-overview--compact" aria-label="Partners summary">
        <DashboardHero metrics={heroMetrics} loading={loading && !rows.length} />
      </section>

      <DashboardSection
        id="partners-directory"
        icon={<ApiOutlined />}
        title="Partner directory"
        subtitle={loading ? 'Loading…' : `${rows.length} partner${rows.length === 1 ? '' : 's'}`}
        collapsible={false}
        variant="content"
        action={(
          <div className="partners-toolbar partners-toolbar--inline">
            <Button size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Create partner
            </Button>
          </div>
        )}
      >
        <Alert
          type="info"
          showIcon
          message="Partner portal and API keys"
          description={
            <span>
              Partners sign in at the partner portal (URL shown on each partner detail).
              They create and revoke inbound API keys there. Admins never see plaintext keys—only masked identifiers here.
            </span>
          }
        />
        {loading && !rows.length ? (
          <TableSkeleton rows={8} columns={7} />
        ) : rows.length === 0 ? (
          <Empty description="No integration partners yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="partners-table-wrap">
            <Table
              rowKey={(r) => String(r.id)}
              loading={loading}
              dataSource={rows}
              columns={columns}
              pagination={false}
              size="small"
            />
          </div>
        )}
      </DashboardSection>

      <Modal
        {...MODAL_PROPS}
        title="Create integration partner"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={submitCreate}
        confirmLoading={creating}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="slug"
            label="Slug"
            rules={[
              { required: true, message: 'Slug is required' },
              { min: 3, max: 64, message: 'Slug must be 3–64 characters' },
              {
                pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                message: 'Lowercase letters, digits, and hyphens only (no leading or trailing hyphen)',
              },
            ]}
          >
            <Input placeholder="e.g. saaspro" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="name"
            label="Display name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 160, message: 'At most 160 characters' },
            ]}
          >
            <Input placeholder="Partner organization name" />
          </Form.Item>
          <Form.Item name="capabilities" label="Capabilities (optional)">
            <CapabilityPickerField catalog={capabilityCatalog} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function copyText(text) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => message.success('Copied'),
    () => message.error('Copy failed'),
  );
}

function PartnerDetail({ partnerId }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [partner, setPartner] = useState(null);
  const [users, setUsers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize] = useState(20);
  const [auditLoading, setAuditLoading] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [tab, setTab] = useState(() => resolvePartnerDetailTabFromSearch(location.search).outer);
  const [paSubtab] = useState(() => resolvePartnerDetailTabFromSearch(location.search).subtab);
  const [paDecisionsFilters] = useState(() => paAgentDecisionsFiltersFromSearch(location.search));
  const [adminModules, setAdminModules] = useState([]);
  const [portalUrl, setPortalUrl] = useState(null);
  const [portalManifest, setPortalManifest] = useState(null);
  const [detailBadges, setDetailBadges] = useState([]);
  const [capabilityCatalog, setCapabilityCatalog] = useState([]);
  const [adminShell, setAdminShell] = useState(DEFAULT_ADMIN_SHELL);
  const [connector, setConnector] = useState(null);
  const [inviteForm] = Form.useForm();

  const showOutboundConnector = useMemo(
    () => partnerHasOutboundConnector(capabilityCatalog),
    [capabilityCatalog],
  );

  const onTabChange = useCallback(
    (key) => {
      setTab(key);
      const sp = new URLSearchParams();
      sp.set('tab', key);
      if (key === 'pa-agent' && paSubtab) sp.set('subtab', paSubtab);
      navigate(
        { pathname: `/admin/integrations/partners/${partnerId}`, search: sp.toString() },
        { replace: true },
      );
    },
    [navigate, partnerId, paSubtab],
  );

  const loadDetail = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await lib.getIntegrationPartner(partnerId);
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Could not load partner') });
        setPartner(null);
        return;
      }
      const data = body?.data ?? body;
      setPartner(data?.partner || data);
      setUsers(Array.isArray(data?.users) ? data.users : []);
      setApiKeys(Array.isArray(data?.api_keys) ? data.api_keys : []);
      setAdminModules(Array.isArray(data?.admin_modules) ? data.admin_modules : []);
      setPortalUrl(data?.portal?.url || null);
      setPortalManifest(data?.portal?.manifest || null);
      setDetailBadges(Array.isArray(data?.badges) ? data.badges : []);
      setCapabilityCatalog(Array.isArray(data?.capability_catalog) ? data.capability_catalog : []);
      setAdminShell(
        Array.isArray(data?.admin_shell) && data.admin_shell.length
          ? data.admin_shell
          : DEFAULT_ADMIN_SHELL,
      );
      setConnector(data?.connector || null);
      const defaultTab = data?.default_admin_tab;
      if (defaultTab && !new URLSearchParams(location.search).get('tab')) {
        setTab(defaultTab);
      }
    } catch (e) {
      notification.error({ message: 'Could not load partner', description: e?.message });
      setPartner(null);
    } finally {
      setLoading(false);
    }
  }, [partnerId, location.search]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const resp = await lib.getIntegrationPartnerAudit(partnerId, {
        page: auditPage,
        page_size: auditPageSize,
      });
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Could not load audit log') });
        setAuditRows([]);
        setAuditTotal(0);
        return;
      }
      const data = body?.data ?? body;
      const rows = Array.isArray(data?.rows)
        ? data.rows
        : Array.isArray(data?.audit)
          ? data.audit
          : Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : [];
      const total = data?.total ?? data?.count ?? rows.length;
      setAuditRows(rows);
      setAuditTotal(Number(total) || rows.length);
    } catch (e) {
      notification.error({ message: 'Could not load audit', description: e?.message });
      setAuditRows([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  }, [partnerId, auditPage, auditPageSize]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    const { outer } = resolvePartnerDetailTabFromSearch(location.search);
    setTab(outer);
  }, [location.search]);

  useEffect(() => {
    if (tab === 'audit') loadAudit();
  }, [tab, loadAudit]);

  const onPartnerStatus = async (checked) => {
    const next = checked ? 'active' : 'disabled';
    const run = async () => {
      setStatusSaving(true);
      try {
        const resp = await lib.patchIntegrationPartner(partnerId, { status: next });
        const body = unwrapBody(resp);
        if (Number(body?.error) && body?.error !== 0) {
          notification.error({ message: apiErrorMessage(body, 'Update failed') });
          return;
        }
        message.success('Partner updated');
        await loadDetail();
      } catch (e) {
        notification.error({ message: 'Update failed', description: e?.message });
      } finally {
        setStatusSaving(false);
      }
    };

    if (!checked) {
      Modal.confirm({
        title: 'Disable partner?',
        content: 'Inbound API keys for this partner will stop working until the partner is re-enabled.',
        okText: 'Disable',
        okButtonProps: { danger: true },
        onOk: run,
      });
    } else {
      await run();
    }
  };

  const onUserStatus = (userRow, next) => {
    Modal.confirm({
      title: `${next === 'disabled' ? 'Disable' : 'Enable'} user?`,
      onOk: async () => {
        try {
          const resp = await lib.patchIntegrationPartnerUser(partnerId, userRow.id, { status: next });
          const body = unwrapBody(resp);
          if (Number(body?.error) && body?.error !== 0) {
            notification.error({ message: apiErrorMessage(body, 'Update failed') });
            return;
          }
          message.success('User updated');
          await loadDetail();
        } catch (e) {
          notification.error({ message: 'Update failed', description: e?.message });
        }
      },
    });
  };

  const onUserRole = (userRow, nextRole) => {
    Modal.confirm({
      title: nextRole === 'admin' ? 'Promote to admin?' : 'Demote to member?',
      content:
        nextRole === 'admin'
          ? 'Admins can invite users and manage portal settings.'
          : 'Members keep portal access but cannot manage the team.',
      onOk: async () => {
        try {
          const resp = await lib.patchIntegrationPartnerUser(partnerId, userRow.id, { role: nextRole });
          const body = unwrapBody(resp);
          if (Number(body?.error) && body?.error !== 0) {
            notification.error({ message: apiErrorMessage(body, 'Could not change role') });
            return;
          }
          message.success('Role updated');
          await loadDetail();
        } catch (e) {
          notification.error({ message: 'Could not change role', description: e?.message });
        }
      },
    });
  };

  const onUserTransferOwnership = (userRow) => {
    Modal.confirm({
      title: `Transfer ownership to ${userRow.email}?`,
      content: 'The current owner becomes admin. The target user must be active.',
      okText: 'Transfer',
      onOk: async () => {
        try {
          const resp = await lib.transferIntegrationPartnerOwnership(partnerId, userRow.id);
          const body = unwrapBody(resp);
          if (Number(body?.error) && body?.error !== 0) {
            notification.error({ message: apiErrorMessage(body, 'Transfer failed') });
            return;
          }
          message.success('Ownership transferred');
          await loadDetail();
        } catch (e) {
          notification.error({ message: 'Transfer failed', description: e?.message });
        }
      },
    });
  };

  const showInviteLinkModal = (link, emailSent, title) => {
    if (!link) return;
    Modal.info({
      title: title || (emailSent ? 'Invitation link (backup)' : 'Share invitation link'),
      width: 560,
      content: (
        <div>
          {!emailSent && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Email delivery failed — copy the link and send it manually."
            />
          )}
          <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
            Invite link (single-use)
          </Text>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} readOnly value={link} />
          <Button type="primary" icon={<CopyOutlined />} style={{ marginTop: 12 }} onClick={() => copyText(link)}>
            Copy link
          </Button>
        </div>
      ),
    });
  };

  const onResendInvite = (userRow) => {
    Modal.confirm({
      title: `Resend invitation to ${userRow.email}?`,
      content: 'A new email will be sent and a fresh invite link issued. The previous link will stop working.',
      okText: 'Resend',
      onOk: async () => {
        try {
          const resp = await lib.resendIntegrationPartnerInvite(partnerId, userRow.id);
          const body = unwrapBody(resp);
          if (Number(body?.error) && body?.error !== 0) {
            notification.error({ message: apiErrorMessage(body, 'Could not resend invitation') });
            return;
          }
          const data = body?.data ?? {};
          const link = data.invite_link;
          const emailSent = data.email_sent !== false;
          message.success(body?.message || (emailSent ? 'Invitation resent' : 'Invitation re-issued'));
          await loadDetail();
          if (link) showInviteLinkModal(link, emailSent, 'Resent invitation link');
        } catch (e) {
          notification.error({ message: 'Could not resend invitation', description: e?.message });
        }
      },
    });
  };

  const onUserDelete = (userRow) => {
    Modal.confirm({
      title: `Remove ${userRow.email}?`,
      content:
        'The user is disabled and sessions are revoked. The record is retained for audit. If the user is the owner, transfer ownership first.',
      okText: 'Remove',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const resp = await lib.deleteIntegrationPartnerUser(partnerId, userRow.id);
          const body = unwrapBody(resp);
          if (Number(body?.error) && body?.error !== 0) {
            notification.error({ message: apiErrorMessage(body, 'Could not remove user') });
            return;
          }
          message.success('User removed');
          await loadDetail();
        } catch (e) {
          notification.error({ message: 'Could not remove user', description: e?.message });
        }
      },
    });
  };

  const submitInvite = async () => {
    try {
      const values = await inviteForm.validateFields();
      setInviting(true);
      const resp = await lib.inviteIntegrationPartnerUser(partnerId, {
        email: String(values.email).trim(),
        name: values.name?.trim() || undefined,
      });
      const body = unwrapBody(resp);
      if (Number(body?.error) && body?.error !== 0) {
        notification.error({ message: apiErrorMessage(body, 'Invite failed') });
        return;
      }
      const data = body?.data ?? {};
      const link = data.invite_link;
      const emailSent = data.email_sent !== false;
      message.success(body?.message || (emailSent ? 'Invitation sent' : 'Invitation created'));
      setInviteOpen(false);
      inviteForm.resetFields();
      await loadDetail();
      if (link) showInviteLinkModal(link, emailSent);
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: 'Invite failed', description: e?.message });
    } finally {
      setInviting(false);
    }
  };

  const renderRoleTag = (role) => {
    if (role === 'owner') {
      return (
        <Tag color="gold" icon={<CrownOutlined />}>
          owner
        </Tag>
      );
    }
    if (role === 'admin') return <Tag color="blue">admin</Tag>;
    if (role === 'member') return <Tag>member</Tag>;
    return <Tag>—</Tag>;
  };

  const getInviteState = (row) => {
    if (row.last_login_at) return null;
    const exp = row.invite_expires_at;
    if (!exp) return null;
    const expDate = new Date(exp);
    if (Number.isNaN(expDate.getTime())) return null;
    const now = Date.now();
    if (expDate.getTime() < now) return { expired: true, expDate };
    return { expired: false, expDate };
  };

  const renderInviteTag = (row) => {
    const state = getInviteState(row);
    if (!state) return null;
    const label = state.expired ? 'Invite expired' : 'Pending invite';
    const color = state.expired ? 'red' : 'orange';
    const tooltip = state.expired
      ? `Invite expired ${state.expDate.toLocaleString()}`
      : `Invite expires ${state.expDate.toLocaleString()}`;
    return (
      <Tooltip title={tooltip}>
        <Tag color={color} icon={<ClockCircleOutlined />} style={{ marginInlineEnd: 0 }}>
          {label}
        </Tag>
      </Tooltip>
    );
  };

  const userColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Name', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (v) => renderRoleTag(v),
    },
    {
      title: 'Status',
      key: 'status',
      width: 200,
      render: (_, row) => (
        <Space size={4} wrap>
          <Tag color={row.status === 'active' ? 'green' : 'default'}>{row.status || '—'}</Tag>
          {renderInviteTag(row)}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, row) => {
      const isOwner = row.role === 'owner';
      const isActive = row.status === 'active';
      const inviteState = getInviteState(row);
      const items = [];
      if (inviteState) {
        items.push({
          key: 'resend',
          label: inviteState.expired ? 'Resend expired invite' : 'Resend invitation',
          icon: <MailOutlined />,
          onClick: () => onResendInvite(row),
        });
      }
      if (!isOwner && row.role !== 'admin') {
        items.push({ key: 'promote', label: 'Promote to admin', onClick: () => onUserRole(row, 'admin') });
      }
      if (!isOwner && row.role === 'admin') {
        items.push({ key: 'demote', label: 'Demote to member', onClick: () => onUserRole(row, 'member') });
      }
      if (!isOwner && isActive) {
        items.push({
          key: 'transfer',
          label: 'Make owner',
          icon: <SwapOutlined />,
          onClick: () => onUserTransferOwnership(row),
        });
      }
      if (!isOwner) {
        items.push({
          key: 'delete',
          label: 'Remove user',
          icon: <DeleteOutlined />,
          danger: true,
          onClick: () => onUserDelete(row),
        });
      }
        return (
          <Space>
            {isActive ? (
              <Button
                type="link"
                danger
                size="small"
                onClick={() => onUserStatus(row, 'disabled')}
                disabled={isOwner}
                title={isOwner ? 'Owner cannot be disabled — transfer ownership first' : undefined}
              >
                Disable
              </Button>
            ) : (
              <Button type="link" size="small" onClick={() => onUserStatus(row, 'active')}>
                Enable
              </Button>
            )}
            {items.length > 0 && (
              <Dropdown menu={{ items }} trigger={['click']}>
                <Button type="text" size="small" icon={<MoreOutlined />} />
              </Dropdown>
            )}
          </Space>
        );
      },
    },
    { title: 'Last login', dataIndex: 'last_login_at', key: 'last_login_at', render: (v) => fmtDateTime(v) },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v) => fmtDateTime(v) },
  ];

  const keyColumns = useMemo(
    () => [
      { title: 'Name', dataIndex: 'name', key: 'name' },
      {
        title: 'Key',
        dataIndex: 'masked_key',
        key: 'masked_key',
        render: (v, row) => <code>{v || row.key_prefix || '—'}</code>,
      },
      {
        title: 'Scopes',
        dataIndex: 'scopes',
        key: 'scopes',
        render: (scopes) =>
          Array.isArray(scopes) && scopes.length ? (
            <Space wrap size={[4, 4]}>
              {scopes.map((s) => (
                <Tag key={s}>{s}</Tag>
              ))}
            </Space>
          ) : (
            '—'
          ),
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (v) => <Tag color={v === 'active' ? 'blue' : 'default'}>{v || '—'}</Tag>,
      },
      { title: 'Last used', dataIndex: 'last_used_at', key: 'last_used_at', render: (v) => fmtDateTime(v) },
      { title: 'Last IP', dataIndex: 'last_used_ip', key: 'last_used_ip', render: (v) => v || '—' },
      { title: 'Created', dataIndex: 'created_at', key: 'created_at', render: (v) => fmtDateTime(v) },
    ],
    [],
  );

  const auditColumns = useMemo(
    () => [
      { title: 'When', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v) => fmtDateTime(v) },
      {
        title: 'Actor',
        key: 'actor',
        width: 140,
        render: (_, row) => (
          <span>
            {row.actor_type || '—'}
            {row.actor_id != null ? ` #${row.actor_id}` : ''}
          </span>
        ),
      },
      { title: 'Action', dataIndex: 'action', key: 'action', width: 200, ellipsis: true },
      {
        title: 'Target',
        key: 'target',
        width: 160,
        ellipsis: true,
        render: (_, row) => {
          const t = row.target_type || row.target_kind;
          const id = row.target_id;
          if (!t && id == null) return '—';
          return (
            <span>
              {t || '—'}
              {id != null ? ` #${id}` : ''}
            </span>
          );
        },
      },
      {
        title: 'Meta',
        dataIndex: 'meta',
        key: 'meta',
        ellipsis: true,
        render: (meta) => {
          if (meta == null) return '—';
          try {
            return typeof meta === 'string' ? meta : JSON.stringify(meta);
          } catch {
            return String(meta);
          }
        },
      },
      { title: 'IP', dataIndex: 'ip', key: 'ip', width: 120, ellipsis: true },
    ],
    [],
  );

  if (loading && !partner) {
    return (
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Partner detail</h1>
            <p className="dash-masthead__meta">Loading partner…</p>
          </div>
        </header>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <h1 className="dash-masthead__title">Partner not found</h1>
            <p className="dash-masthead__meta">
              <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/integrations/partners')}>
                All partners
              </Button>
            </p>
          </div>
        </header>
        <Empty description="This partner may have been removed or the ID is invalid.">
          <Button type="primary" onClick={() => navigate('/admin/integrations/partners')}>
            Back to list
          </Button>
        </Empty>
      </div>
    );
  }

  const partnerActive = partner.status === 'active';
  const shellTabLabels = { users: 'Users', keys: 'API keys', audit: 'Audit' };
  const sortedModules = [...adminModules].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const tabOptions = [
    ...adminShell
      .filter((key) => shellTabLabels[key])
      .map((key) => ({ label: shellTabLabels[key], value: key })),
    ...sortedModules.map((mod) => ({ label: mod.label, value: mod.key })),
  ];

  const activeUsers = users.filter((u) => u.status === 'active').length;
  const activeKeys = apiKeys.filter((k) => k.status === 'active').length;

  const heroMetrics = [
    {
      key: 'users',
      title: 'Portal users',
      value: activeUsers,
      color: '#3497D3',
      icon: <TeamOutlined />,
      subtitle: `${users.length} total`,
    },
    {
      key: 'keys',
      title: 'API keys',
      value: activeKeys,
      color: '#0ea5e9',
      icon: <KeyOutlined />,
      subtitle: `${apiKeys.length} registered`,
    },
    {
      key: 'status',
      title: 'Status',
      value: partnerActive ? 'Active' : 'Disabled',
      color: partnerActive ? '#49B870' : '#94a3b8',
      icon: <SafetyCertificateOutlined />,
      subtitle: partner.slug,
    },
    {
      key: 'capabilities',
      title: 'Capabilities',
      value: detailBadges.length,
      color: '#722ed1',
      icon: <ApiOutlined />,
      subtitle: 'Enabled modules',
    },
  ].filter((m) => hasMetricValue(m.value));

  const refreshDetail = () => {
    loadDetail();
    if (tab === 'audit') loadAudit();
  };

  return (
    <div className="dashboard-page-stack">
      <header className="dash-masthead">
        <div className="dash-masthead__lead">
          <h1 className="dash-masthead__title">{partner.name || partner.slug}</h1>
          <p className="dash-masthead__meta">
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/integrations/partners')}>
              Integration partners
            </Button>
            <span className="dash-masthead__sep" aria-hidden>·</span>
            <span>Partner ID {partner.id}</span>
          </p>
        </div>
      </header>

      <section className="partners-overview dash-overview dash-overview--compact" aria-label="Partner summary">
        <DashboardHero metrics={heroMetrics} loading={loading && !users.length && !apiKeys.length} />
      </section>

      {!partnerActive ? (
        <Alert
          type="warning"
          showIcon
          message="Partner disabled"
          description="Inbound API keys are rejected until this partner is re-enabled."
        />
      ) : null}

      <DashboardSection
        id="partner-profile"
        icon={<ApiOutlined />}
        title="Partner profile"
        subtitle={partner.slug}
        collapsible={false}
        variant="content"
        action={(
          <div className="partners-profile-actions">
            <Text type="secondary">Status</Text>
            <Switch
              checked={partnerActive}
              loading={statusSaving}
              checkedChildren="active"
              unCheckedChildren="disabled"
              onChange={onPartnerStatus}
            />
            <Button size="small" icon={<ReloadOutlined />} onClick={loadDetail} loading={loading}>
              Refresh
            </Button>
          </div>
        )}
      >
        <div className="partners-summary-grid">
          <div className="partners-summary-item">
            <span className="partners-summary-item__label">Display name</span>
            <span className="partners-summary-item__value">{partner.name || '—'}</span>
          </div>
          <div className="partners-summary-item">
            <span className="partners-summary-item__label">Slug</span>
            <span className="partners-summary-item__value"><code>{partner.slug || '—'}</code></span>
          </div>
          <div className="partners-summary-item">
            <span className="partners-summary-item__label">Capabilities</span>
            <span className="partners-summary-item__value">{renderBadges(detailBadges) || '—'}</span>
          </div>
          <div className="partners-summary-item">
            <span className="partners-summary-item__label">Created</span>
            <span className="partners-summary-item__value">{fmtDateTime(partner.created_at)}</span>
          </div>
        </div>
        <Alert
          type="info"
          showIcon
          message="API keys are managed in the partner portal"
          description={
            <span>
              Keys are created and revoked by the partner at{' '}
              {portalUrl ? (
                <Link href={portalUrl} target="_blank" rel="noreferrer">
                  Open partner portal
                </Link>
              ) : (
                'the partner portal'
              )}
              . This screen shows masked keys and usage only.
            </span>
          }
        />
        {capabilityCatalog.length > 0 ? (
          <div>
            <Text strong>Capabilities</Text>
            <PartnerCapabilitiesPanel
              partnerId={partnerId}
              capabilityCatalog={capabilityCatalog}
              onSaved={loadDetail}
            />
          </div>
        ) : null}
        {showOutboundConnector ? (
          <div>
            <Text strong>Outbound connector</Text>
            <PaAgentOutboundConnectorPanel
              partnerId={partnerId}
              connector={connector}
              onSaved={loadDetail}
            />
          </div>
        ) : null}
        {portalManifest ? (
          <div>
            <Text strong>Partner portal preview</Text>
            <PortalManifestPreview manifest={portalManifest} />
          </div>
        ) : null}
      </DashboardSection>

      {tabOptions.length > 0 ? (
        <div className="dash-tab-strip" role="tablist" aria-label="Partner sections">
          <Segmented value={tab} onChange={onTabChange} options={tabOptions} />
        </div>
      ) : null}

      {tab === 'users' ? (
        <DashboardSection
          id="partner-users"
          icon={<TeamOutlined />}
          title="Portal users"
          subtitle={`${users.length} user${users.length === 1 ? '' : 's'}`}
          collapsible={false}
          variant="content"
          action={(
            <div className="partners-toolbar partners-toolbar--inline">
              <Button size="small" icon={<ReloadOutlined />} onClick={refreshDetail} loading={loading}>
                Refresh
              </Button>
              <Button
                type="primary"
                size="small"
                icon={<UserAddOutlined />}
                onClick={() => setInviteOpen(true)}
                disabled={!partnerActive}
              >
                Invite user
              </Button>
            </div>
          )}
        >
          {!partnerActive ? (
            <Alert
              type="warning"
              showIcon
              message="This partner is disabled — invite is blocked until the partner is active again."
            />
          ) : null}
          <div className="partners-table-wrap">
            <Table rowKey={(r) => String(r.id)} dataSource={users} columns={userColumns} pagination={false} size="small" />
          </div>
        </DashboardSection>
      ) : null}

      {tab === 'keys' ? (
        <DashboardSection
          id="partner-keys"
          icon={<KeyOutlined />}
          title="API keys"
          subtitle={`${apiKeys.length} key${apiKeys.length === 1 ? '' : 's'} · masked identifiers only`}
          collapsible={false}
          variant="content"
          action={(
            <Button size="small" icon={<ReloadOutlined />} onClick={refreshDetail} loading={loading}>
              Refresh
            </Button>
          )}
        >
          <div className="partners-table-wrap">
            <Table rowKey={(r) => String(r.id)} dataSource={apiKeys} columns={keyColumns} pagination={false} size="small" />
          </div>
        </DashboardSection>
      ) : null}

      {tab === 'audit' ? (
        <DashboardSection
          id="partner-audit"
          icon={<UnorderedListOutlined />}
          title="Audit log"
          subtitle={
            auditLoading
              ? 'Loading…'
              : `${auditTotal} event${auditTotal === 1 ? '' : 's'}`
          }
          collapsible={false}
          variant="content"
          action={(
            <Button size="small" icon={<ReloadOutlined />} onClick={loadAudit} loading={auditLoading}>
              Refresh
            </Button>
          )}
        >
          <div className="partners-table-wrap">
            <Table
              rowKey={(r, i) => String(r.id ?? `${auditPage}-${i}`)}
              loading={auditLoading}
              dataSource={auditRows}
              columns={auditColumns}
              size="small"
              pagination={{
                current: auditPage,
                pageSize: auditPageSize,
                total: auditTotal,
                showSizeChanger: false,
                onChange: (p) => setAuditPage(p),
              }}
            />
          </div>
        </DashboardSection>
      ) : null}

      {sortedModules.map((mod) => (
        tab === mod.key ? (
          <DashboardSection
            key={mod.key}
            id={`partner-mod-${mod.key}`}
            icon={MODULE_ICONS[mod.icon] ? React.createElement(MODULE_ICONS[mod.icon]) : <RobotOutlined />}
            title={mod.label}
            subtitle="Capability module"
            collapsible={false}
            variant="content"
          >
            {renderAdminModule(mod.key, {
              partnerId: Number(partnerId),
              module: mod,
              initialSubtab: paSubtab,
              initialDecisionsFilters: paDecisionsFilters,
            })}
          </DashboardSection>
        ) : null
      ))}

      <Modal
        {...MODAL_PROPS}
        title="Invite partner user"
        open={inviteOpen}
        onCancel={() => {
          setInviteOpen(false);
          inviteForm.resetFields();
        }}
        onOk={submitInvite}
        confirmLoading={inviting}
        destroyOnClose
      >
        <Form form={inviteForm} layout="vertical">
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
            <Input placeholder="dev@partner.com" />
          </Form.Item>
          <Form.Item name="name" label="Name (optional)">
            <Input placeholder="Display name" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default function PartnersAdminPage() {
  const { partnerId } = useParams();
  // admin.* only: every /v2/admin/integrations route is guarded by admin.*, so the
  // catalog module key (which resolves to admin.role_management) would be too loose.
  const { hasAnyPermissionWithLegacy } = usePermissions();
  const isAdmin = hasAnyPermissionWithLegacy(['admin.*'], [1]);
  const [capabilityCatalog, setCapabilityCatalog] = useState([]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const resp = await lib.listIntegrationCapabilities();
        if (!cancelled) setCapabilityCatalog(normalizeCapabilitiesCatalog(resp));
      } catch {
        if (!cancelled) setCapabilityCatalog([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="partners-dashboard">
        <div className="partners-unauthorized">
          <Empty
            description={(
              <>
                <Text strong>Admins only</Text>
                <div style={{ color: 'rgba(0,0,0,0.55)' }}>
                  Integration partner administration is restricted to administrators.
                </div>
              </>
            )}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="partners-dashboard">
      {partnerId ? (
        <PartnerDetail partnerId={partnerId} />
      ) : (
        <PartnersList capabilityCatalog={capabilityCatalog} />
      )}
    </div>
  );
}
